import * as z from "zod";

export enum PerformanceType {
  CAMPAIGN = "campaign",
  AUTOMATION = "automation",
}

export enum PerformanceChannel {
  EMAIL = "email",
  TEXT = "text",
  OTHER = "other",
}

export enum PerformanceAuthor {
  SHOPPER_AGENT = "shopper_agent",
  MANUAL = "manual",
}

export enum PerformanceAudience {
  /** The agent's sends skipped the human review queue. */
  SUBSCRIBERS = "subscribers",
  /** A reviewer approved or edited the agent's sends. */
  NON_SUBSCRIBERS = "non_subscribers",
  OTHER = "other",
}

/** What made the sale: a new order, or an exchange or edit adding to one. */
export enum PerformanceSaleKind {
  NEW_ORDER = "new_order",
  EXCHANGE = "exchange",
  EDIT = "edit",
  OTHER = "other",
}

export const performanceRowSchema = z.object({
  type: z.enum(PerformanceType),
  channel: z.enum(PerformanceChannel),
  author: z.enum(PerformanceAuthor),
  /** Manual sends go to subscribers. */
  audience: z.enum(PerformanceAudience),
  saleKind: z.enum(PerformanceSaleKind),
  revenue: z.number(),
  orders: z.number(),
});
export type PerformanceRow = z.infer<typeof performanceRowSchema>;

export const PERFORMANCE_TYPE_LABELS: Record<PerformanceType, string> = {
  [PerformanceType.CAMPAIGN]: "Campaigns",
  [PerformanceType.AUTOMATION]: "Automations",
};

export const PERFORMANCE_CHANNEL_LABELS: Record<PerformanceChannel, string> = {
  [PerformanceChannel.EMAIL]: "Email",
  [PerformanceChannel.TEXT]: "Text",
  [PerformanceChannel.OTHER]: "Other",
};

export const PERFORMANCE_AUTHOR_LABELS: Record<PerformanceAuthor, string> = {
  [PerformanceAuthor.SHOPPER_AGENT]: "Commerce agent",
  [PerformanceAuthor.MANUAL]: "Manual",
};

export const PERFORMANCE_AUDIENCE_LABELS: Record<PerformanceAudience, string> =
  {
    [PerformanceAudience.SUBSCRIBERS]: "Subscribers",
    [PerformanceAudience.NON_SUBSCRIBERS]: "Non-subscribers (Recover)",
    [PerformanceAudience.OTHER]: "Other",
  };

export const PERFORMANCE_SALE_KIND_LABELS: Record<PerformanceSaleKind, string> =
  {
    [PerformanceSaleKind.NEW_ORDER]: "New orders",
    [PerformanceSaleKind.EXCHANGE]: "Exchanges",
    [PerformanceSaleKind.EDIT]: "Order edits",
    [PerformanceSaleKind.OTHER]: "Other",
  };

export interface PerformanceNode {
  id: string;
  label: string;
  depth: number;
  revenue: number;
  /** Revenue on the saved settings, or null when the view matches them. */
  defaultRevenue: number | null;
  children: PerformanceNode[];
}

interface Segment {
  key: string;
  label: string;
  order: number;
}

export enum PerformanceDimension {
  TYPE = "type",
  CHANNEL = "channel",
  AUTHOR = "author",
  AUDIENCE = "audience",
  SALE_KIND = "saleKind",
}

export const PERFORMANCE_DIMENSIONS = Object.values(PerformanceDimension);

/** What the page groups by before anyone changes it. */
export const PERFORMANCE_DEFAULT_GROUP_BY: readonly PerformanceDimension[] = [
  PerformanceDimension.TYPE,
  PerformanceDimension.CHANNEL,
  PerformanceDimension.AUTHOR,
  PerformanceDimension.AUDIENCE,
];

export const PERFORMANCE_DIMENSION_LABELS: Record<
  PerformanceDimension,
  string
> = {
  [PerformanceDimension.TYPE]: "Type",
  [PerformanceDimension.CHANNEL]: "Channel",
  [PerformanceDimension.AUTHOR]: "Author",
  [PerformanceDimension.AUDIENCE]: "Audience",
  [PerformanceDimension.SALE_KIND]: "Sale kind",
};

/** Each dimension's values and their labels, in the page's order. */
export const PERFORMANCE_VALUE_LABELS: {
  [D in PerformanceDimension]: Record<string, string>;
} = {
  [PerformanceDimension.TYPE]: PERFORMANCE_TYPE_LABELS,
  [PerformanceDimension.CHANNEL]: PERFORMANCE_CHANNEL_LABELS,
  [PerformanceDimension.AUTHOR]: PERFORMANCE_AUTHOR_LABELS,
  [PerformanceDimension.AUDIENCE]: PERFORMANCE_AUDIENCE_LABELS,
  [PerformanceDimension.SALE_KIND]: PERFORMANCE_SALE_KIND_LABELS,
};

/** Values to keep per dimension; a dimension left out keeps everything. */
export type PerformanceFilters = Partial<
  Record<PerformanceDimension, readonly string[]>
>;

export function filterPerformanceRows(
  rows: readonly PerformanceRow[],
  filters: PerformanceFilters,
): PerformanceRow[] {
  return rows.filter((row) =>
    PERFORMANCE_DIMENSIONS.every((dimension) => {
      const keep = filters[dimension];
      const value = row[dimension];
      return !keep?.length || keep.includes(value);
    }),
  );
}

/** The grouped dimensions in the page's fixed order: Type, Channel,
 *  Author, Audience, Sale kind. */
export function sortDimensions(
  dimensions: readonly PerformanceDimension[],
): PerformanceDimension[] {
  return PERFORMANCE_DIMENSIONS.filter((dimension) =>
    dimensions.includes(dimension),
  );
}

/** The row's place in the tree, one level per grouped dimension. Manual
 *  sends skip Audience under Author, since they only go to subscribers. */
function pathOf(
  row: PerformanceRow,
  groupBy: readonly PerformanceDimension[],
): Segment[] {
  const levels = sortDimensions(groupBy);
  return levels.flatMap((dimension) => {
    const value = row[dimension];
    if (
      dimension === PerformanceDimension.AUDIENCE &&
      levels.includes(PerformanceDimension.AUTHOR) &&
      row.author === PerformanceAuthor.MANUAL
    ) {
      return [];
    }
    const labels = PERFORMANCE_VALUE_LABELS[dimension];
    return [
      {
        key: `${dimension}:${value}`,
        label: labels[value] ?? value,
        order: Object.keys(labels).indexOf(value),
      },
    ];
  });
}

interface MutableNode extends PerformanceNode {
  order: number;
  children: MutableNode[];
}

export function buildPerformanceTree(
  rows: readonly PerformanceRow[],
  defaultRows: readonly PerformanceRow[] | null,
  groupBy: readonly PerformanceDimension[] = PERFORMANCE_DEFAULT_GROUP_BY,
): PerformanceNode[] {
  const roots: MutableNode[] = [];
  const add = (row: PerformanceRow, field: "revenue" | "defaultRevenue") => {
    let level = roots;
    let id = "";
    for (const [depth, part] of pathOf(row, groupBy).entries()) {
      id = `${id}/${part.key}`;
      let node = level.find((candidate) => candidate.id === id);
      if (!node) {
        node = {
          id,
          label: part.label,
          depth,
          order: part.order,
          revenue: 0,
          defaultRevenue: defaultRows ? 0 : null,
          children: [],
        };
        level.push(node);
      }
      node[field] = (node[field] ?? 0) + row.revenue;
      level = node.children;
    }
  };
  for (const row of rows) {
    add(row, "revenue");
  }
  for (const row of defaultRows ?? []) {
    add(row, "defaultRevenue");
  }
  const sort = (nodes: MutableNode[]): PerformanceNode[] =>
    [...nodes]
      .sort((a, b) => a.order - b.order)
      .map((node) => ({
        id: node.id,
        label: node.label,
        depth: node.depth,
        revenue: node.revenue,
        defaultRevenue: node.defaultRevenue,
        children: sort(node.children),
      }));
  return sort(roots);
}

/** The rows a table shows: every node whose ancestors are all expanded. */
export function visibleNodes<T extends { id: string; children: readonly T[] }>(
  nodes: readonly T[],
  collapsed: ReadonlySet<string>,
): T[] {
  return nodes.flatMap((node) => [
    node,
    ...(collapsed.has(node.id) ? [] : visibleNodes(node.children, collapsed)),
  ]);
}
